/**
 * TOSS Web Application — Client Portal auth API (`/api/client/auth/*`)
 *
 * The external customer portal's own auth surface. Phase 0 already shipped every endpoint used
 * here (`ClientAuthController`), so this module only types them.
 *
 * Why a separate module instead of a branch inside `api/auth.api.ts`: resolving "the current
 * audience" is meaningless before a login has succeeded — `TokenStorage.getAudience()` still
 * says `internal` while the portal's login request is in flight. `auth.api.ts` says as much in
 * its own comment and leaves the client login form to this module (release Р1).
 *
 * The token/refresh request and response shapes are audience-independent and reused as-is; only
 * the user block of the login response is client-specific (see {@link ClientAuthUserDto}).
 *
 * Contract: vault/specs/client-portal/prep-work/r1-api-contract.md §2.
 * There are deliberately NO `fcm-token` functions — the portal has no push notifications in Р1
 * (decision Р12, rev. 2026-08-18); `ClientAuthController` has no such action and calling one
 * would 404.
 */

import apiClient from '@/api/client';
import { TokenStorage } from '@/api/interceptors';
import { CLIENT_API_BASE } from './clientPortal.types';
import type { UserRole, UserStatus } from '@/types/enums';
import type {
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  LoginRequest,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ResetPasswordRequest,
  SuccessResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '@/types';
// Type-only: the change-password body is genuinely shared with the internal audience
// (one `ChangePasswordRequest` on the backend), and a type import leaves no runtime edge.
import type { ChangePasswordRequest } from '@/api/auth.api';

// ============================================================================
// Endpoints
// ============================================================================

const ENDPOINTS = {
  LOGIN: `${CLIENT_API_BASE}/auth/login`,
  REFRESH: `${CLIENT_API_BASE}/auth/refresh`,
  FORGOT_PASSWORD: `${CLIENT_API_BASE}/auth/forgot-password`,
  VERIFY_OTP: `${CLIENT_API_BASE}/auth/verify-otp`,
  RESET_PASSWORD: `${CLIENT_API_BASE}/auth/reset-password`,
  LOGOUT: `${CLIENT_API_BASE}/auth/logout`,
  CHANGE_PASSWORD: `${CLIENT_API_BASE}/auth/change-password`,
} as const;

// ============================================================================
// Types
// ============================================================================

/**
 * The user block of `POST /api/client/auth/login` — mirrors
 * `src/Toss.Api/Features/Auth/Client/ClientAuthUserDto.cs` field for field.
 *
 * It is NOT the internal `AuthUserDto`: `ptoId`, `ptoName`, `isMentor`, `mentorId`,
 * `isRulesAccepted`, `isPushEnabled`, `photoUrl`, `fcmToken` and `createdAt` are staff concerns
 * and never cross onto a client route.
 *
 * `role` stays in the contract because the frontend derives the session's API space from it
 * (`authStore.setAuth()` → `TokenStorage.setAudience()`); dropping it would send the portal's
 * own requests to `/api/lk/*`. `customerId` / `customerName` are non-nullable on the backend
 * record — a client account with no company binding cannot log in at all (403
 * `CLIENT_COMPANY_NOT_BOUND`).
 */
export interface ClientAuthUserDto {
  id: string;
  email: string;
  fullName: string;
  phoneNumber?: string;
  /** Always `UserRole.Client`. */
  role: UserRole;
  status: UserStatus;
  customerId: string;
  customerName: string;
}

/**
 * Response of `POST /api/client/auth/login`. Same token fields as the internal `LoginResponse`
 * — those are the audience-independent half — with a client-only user block.
 */
export interface ClientLoginResponse {
  accessToken: string;
  refreshToken: string;
  /** ISO-8601 UTC. */
  expiresAt: string;
  user: ClientAuthUserDto;
}

// ============================================================================
// Anonymous endpoints
// ============================================================================

/**
 * Log a customer-portal account in.
 *
 * The caller is responsible for persisting the session — `authStore.setAuth()` stores the
 * tokens AND records the audience (`TokenStorage.setAudience('client')`, derived from
 * `user.role`). Skipping that step leaves every subsequent request pointed at `/api/lk/*`.
 *
 * @throws 401 when the credentials are wrong; 403 when the account belongs to another audience
 * or has no company binding.
 */
export async function clientLogin(
  credentials: LoginRequest
): Promise<ClientLoginResponse> {
  const response = await apiClient.post<ClientLoginResponse>(ENDPOINTS.LOGIN, credentials);
  return response.data;
}

/**
 * Exchange a refresh token for a new access token.
 *
 * Normally driven by the response interceptor, which already resolves this path through
 * `getAudienceApiBase()`. Exposed here for completeness and for an explicit refresh.
 *
 * @throws 403 when the account's company membership was revoked since the token was issued.
 */
export async function refreshClientToken(
  data: RefreshTokenRequest
): Promise<RefreshTokenResponse> {
  const response = await apiClient.post<RefreshTokenResponse>(ENDPOINTS.REFRESH, data);
  return response.data;
}

/** Start password recovery — sends an OTP code to the account's email. */
export async function clientForgotPassword(
  data: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> {
  const response = await apiClient.post<ForgotPasswordResponse>(
    ENDPOINTS.FORGOT_PASSWORD,
    data
  );
  return response.data;
}

/**
 * Verify the OTP code from {@link clientForgotPassword}.
 *
 * Answers 200 with `success: false` (and `remainingAttempts`) on a wrong code — check the
 * payload, not the HTTP status.
 */
export async function clientVerifyOtp(data: VerifyOtpRequest): Promise<VerifyOtpResponse> {
  const response = await apiClient.post<VerifyOtpResponse>(ENDPOINTS.VERIFY_OTP, data);
  return response.data;
}

/** Set a new password using the reset token returned by {@link clientVerifyOtp}. */
export async function clientResetPassword(
  data: ResetPasswordRequest
): Promise<SuccessResponse> {
  const response = await apiClient.post<SuccessResponse>(ENDPOINTS.RESET_PASSWORD, data);
  return response.data;
}

// ============================================================================
// Authenticated endpoints
// ============================================================================

/**
 * Change the password of the signed-in portal account.
 *
 * `skipAuthRefresh` is what keeps a wrong CURRENT password from ending the session: this
 * endpoint answers 401 for bad input, and without the flag the shared interceptor reads that
 * as a dead session, refreshes, retries, gets 401 again and logs the customer out of every
 * tab (see `interceptors.ts` for the full sequence). The flag is opt-in per request — no
 * other caller's behaviour changes.
 */
export async function clientChangePassword(
  data: ChangePasswordRequest
): Promise<SuccessResponse> {
  const response = await apiClient.post<SuccessResponse>(ENDPOINTS.CHANGE_PASSWORD, data, {
    skipAuthRefresh: true,
  } as Parameters<typeof apiClient.post>[2]);
  return response.data;
}

/**
 * Log out: invalidate the refresh token server-side, then clear local tokens.
 *
 * Local tokens are cleared in `finally`, exactly like the internal `authApi.logout()` — a
 * server-side failure must never leave the browser holding a session it believes is valid.
 * There is no FCM unregistration step: the portal has no push (decision Р12).
 */
export async function clientLogout(): Promise<void> {
  try {
    await apiClient.post(ENDPOINTS.LOGOUT);
  } catch {
    // Ignore — we are logging out regardless.
  } finally {
    TokenStorage.clearTokens();
  }
}

// ============================================================================
// Namespace Export
// ============================================================================

/**
 * Client auth API namespace.
 *
 * @example
 * ```ts
 * import { clientAuthApi } from '@/api/clientPortal/clientAuth.api';
 *
 * const session = await clientAuthApi.login({ email, password });
 * ```
 */
export const clientAuthApi = {
  login: clientLogin,
  refreshToken: refreshClientToken,
  forgotPassword: clientForgotPassword,
  verifyOtp: clientVerifyOtp,
  resetPassword: clientResetPassword,
  changePassword: clientChangePassword,
  logout: clientLogout,
} as const;
